// src/pages/LiveClass.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db, rtdb } from "../firebase";
import { doc, getDoc, updateDoc, collection, getDocs } from "firebase/firestore";
import { ref, onValue, set, off, push, remove, onDisconnect, update, get } from "firebase/database";
import { useAuth } from "../AuthContext";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import { signOut } from "firebase/auth";
import { LOGO_SRC } from "../images";
import "./LiveClass.css";

const STARTING_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

const PIECES = [
  { code: "wK", sym: "♔", label: "White King" },
  { code: "wQ", sym: "♕", label: "White Queen" },
  { code: "wR", sym: "♖", label: "White Rook" },
  { code: "wB", sym: "♗", label: "White Bishop" },
  { code: "wN", sym: "♘", label: "White Knight" },
  { code: "wP", sym: "♙", label: "White Pawn" },
  { code: "bK", sym: "♚", label: "Black King" },
  { code: "bQ", sym: "♛", label: "Black Queen" },
  { code: "bR", sym: "♜", label: "Black Rook" },
  { code: "bB", sym: "♝", label: "Black Bishop" },
  { code: "bN", sym: "♞", label: "Black Knight" },
  { code: "bP", sym: "♟", label: "Black Pawn" },
];

// ── Tooltip wrapper ──────────────────────────────────────────────
function Tip({ title, children, side }) {
  return (
    <div className={`tip-wrap${side === "right" ? " tip-right" : ""}`} data-tip={title}>
      {children}
    </div>
  );
}

// react-chessboard < v5 passes squares as strings ("e4");
// v5 passes objects ({ square, piece }). Accept both so an upgrade
// never silently kills click-to-move for students.
function sqName(arg) {
  return typeof arg === "string" ? arg : arg?.square;
}

const ADMIN_MARK_COLOR = "rgba(255, 215, 0, 0.5)";    // gold squares (default)
const STUDENT_MARK_COLOR = "rgba(100, 200, 255, 0.5)"; // blue squares
const ADMIN_ARROW_COLOR = "rgb(255, 170, 0)";          // gold arrows (default)
const STUDENT_ARROW_COLOR = "rgb(80, 180, 255)";       // blue arrows

// Coach modifier colors — Alt = red, Alt+Shift = blue, plain = gold
const MARK_COLORS = {
  default: "rgba(255, 215, 0, 0.5)",   // gold
  alt:     "rgba(235, 70, 70, 0.55)",  // red
  altshift:"rgba(70, 130, 255, 0.55)", // blue
};
const ARROW_COLORS = {
  default: "rgb(255, 170, 0)",   // gold
  alt:     "rgb(230, 60, 60)",   // red
  altshift:"rgb(70, 130, 255)",  // blue
};
function pickColorKey(e) {
  // Alt+Shift → blue, Alt → red, otherwise gold
  if (e && e.altKey && e.shiftKey) return "altshift";
  if (e && e.altKey) return "alt";
  return "default";
}

// ── Move-tree helpers ────────────────────────────────────────────
// The tree is a plain id→node map so it round-trips through Firebase
// RTDB without any special handling. Root always has id "r".
let nodeSeq = 0;
function newNodeId() {
  nodeSeq += 1;
  return `n${Date.now().toString(36)}${nodeSeq.toString(36)}`;
}

function freshTree(rootFen) {
  return {
    nodes: { r: { id: "r", san: null, fen: rootFen, parent: null, children: [], ply: 0 } },
    tipId: "r",
  };
}

// RTDB strips empty arrays and nulls, and can hand arrays back as objects.
// Normalise everything on read so the rest of the code never has to care.
function normalizeNodes(raw, fallbackFen) {
  if (!raw || !raw.r) return freshTree(fallbackFen || STARTING_FEN).nodes;
  const out = {};
  Object.entries(raw).forEach(([id, n]) => {
    if (!n) return;
    const kids = Array.isArray(n.children)
      ? n.children.filter(Boolean)
      : Object.values(n.children || {}).filter(Boolean);
    out[id] = {
      id,
      san: n.san ?? null,
      fen: n.fen || fallbackFen || STARTING_FEN,
      parent: n.parent ?? null,
      children: kids,
      ply: typeof n.ply === "number" ? n.ply : 0,
    };
  });
  if (!out.r) return freshTree(fallbackFen || STARTING_FEN).nodes;
  // Drop any child pointer whose node went missing, so rendering can't crash.
  Object.values(out).forEach(n => { n.children = n.children.filter(c => out[c]); });
  return out;
}

// Rebuild a tree from an old-format flat SAN list, so classes that were
// already running (or PGN imports) keep working.
function treeFromSans(rootFen, sans) {
  const { nodes } = freshTree(rootFen);
  const g = new Chess();
  try { g.load(rootFen); } catch {}
  let cur = "r";
  (sans || []).forEach((san, i) => {
    let f = nodes[cur].fen;
    try { if (g.move(san)) f = g.fen(); } catch {}
    const id = `s${i}`;
    nodes[id] = { id, san, fen: f, parent: cur, children: [], ply: i + 1 };
    nodes[cur].children.push(id);
    cur = id;
  });
  return { nodes, tipId: cur };
}

// Path of ids from the root down to `id`.
function pathTo(nodes, id) {
  const out = [];
  let cur = id;
  let guard = 0;
  while (cur && nodes[cur] && guard++ < 2000) {
    out.unshift(cur);
    cur = nodes[cur].parent;
  }
  return out.length ? out : ["r"];
}

// Follow children[0] from `id` to the end of that line.
function lineEnd(nodes, id) {
  let cur = id;
  let guard = 0;
  while (nodes[cur]?.children?.length && guard++ < 2000) cur = nodes[cur].children[0];
  return cur;
}

// Main line SANs — kept in the board record for backwards compatibility
// (and so the old PGN export path still has something sensible to read).
function mainlineSans(nodes) {
  const out = [];
  let cur = nodes.r?.children?.[0];
  let guard = 0;
  while (cur && nodes[cur] && guard++ < 2000) {
    out.push(nodes[cur].san);
    cur = nodes[cur].children?.[0];
  }
  return out;
}

// PGN with variations in brackets, exactly like lichess exports.
// A variation is an alternative to the move it sits beside, so it is written
// immediately AFTER that move:  3. Bc4 Bc5 (3... Nf6 4. d3)
function treeToPgn(nodes) {
  function walk(startId) {
    const parts = [];
    let id = startId;
    let needNum = true;
    let guard = 0;
    while (id && nodes[id] && guard++ < 2000) {
      const n = nodes[id];
      const no = Math.ceil(n.ply / 2);
      if (n.ply % 2 === 1) parts.push(`${no}.`);
      else if (needNum) parts.push(`${no}...`);
      parts.push(n.san);
      needNum = false;
      // Siblings listed after me are the alternatives to THIS move.
      const sibs = nodes[n.parent]?.children || [];
      if (sibs[0] === id && sibs.length > 1) {
        sibs.slice(1).forEach(vid => { parts.push(`(${walk(vid)})`); needNum = true; });
      }
      id = (n.children || [])[0];
    }
    return parts.join(" ");
  }
  return walk(nodes.r?.children?.[0]);
}

// VIDEO: audio/video runs through our own self-hosted Jitsi server
// (143-244-135-123.sslip.io). Presence, permissions, the board and chat
// all stay on Firebase, exactly as before.
const MAX_STUDENTS = 6;
const PERMS = ["none", "white", "black", "both"];
const PERM_ICON = { none: "🔒", white: "♔", black: "♚", both: "♟" };

export default function LiveClass() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user, userRole, userData } = useAuth();
  // Coaches get the full teaching interface, identical to the admin's.
  // Every teaching control in this file keys off `isAdmin`, so including
  // "coach" here lights all of them up at once.
  const isAdmin = userRole === "admin" || userRole === "coach";

  // Class
  const [classData, setClassData] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(null);

  // Chess
  const [game, setGame] = useState(new Chess());
  const [fen, setFen] = useState(STARTING_FEN);
  // ── Move TREE (lichess-style variations) ──────────────────────
  // Instead of a flat list of SANs we keep a tree of nodes. Going back and
  // playing a different move no longer erases the old line — it becomes a
  // sibling (a variation), rendered in brackets just like lichess.
  //   node = { id, san, fen, parent, children:[ids], ply }
  // children[0] is the main continuation; children[1..] are variations.
  const [nodes, setNodes] = useState(() => freshTree(STARTING_FEN).nodes);
  const [currentId, setCurrentId] = useState("r");
  const [allowIllegal, setAllowIllegal] = useState(false);
  const [boardOrientation, setBoardOrientation] = useState("white");
  // Per-student move permission, keyed by uid: { uid: "none"|"white"|"black"|"both" }
  const [permissions, setPermissions] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [showHints, setShowHints] = useState(false); // show move hints to student
  const [selectedEditPiece, setSelectedEditPiece] = useState(null);
  const [pgnInput, setPgnInput] = useState("");
  const [fenInput, setFenInput] = useState("");
  const [pgnMsg, setPgnMsg] = useState("");
  const [gameResult, setGameResult] = useState(null); // null | { label, reason }
  const [showResult, setShowResult] = useState(false);

  // Right panel
  const [rightTab, setRightTab] = useState("moves"); // moves | chat | options

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const chatEndRef = useRef(null);

  // Video — full mesh
  const [callStatus, setCallStatus] = useState("waiting"); // local camera status: waiting|ready|error
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const myVideoRef = useRef(null);
  const streamRef = useRef(null);
  const [participants, setParticipants] = useState({}); // uid -> { name, role, joinedAt } (includes self)
  const [tileSearch, setTileSearch] = useState("");      // coach: filter participant tiles by name
  const [remoteStreams, setRemoteStreams] = useState({}); // uid -> MediaStream
  const [peerStates, setPeerStates] = useState({});       // uid -> "connecting" | "connected"

  // (Background-effects feature removed — video now runs through our own
  //  Jitsi server. The custom MediaPipe pipeline is gone.)
  const camOnRef = useRef(true);

  const [selectedSquare, setSelectedSquare] = useState(null);
  // Annotations are stored per-class in RTDB and rendered as *controlled*
  // overlays, so they persist through left clicks and are visible to both
  // sides. Each entry carries an `owner` so students can't remove the
  // admin's marks. squares: { e4: {backgroundColor, owner} }
  // arrows: { e2e4: {from, to, color, owner} }
  const [rightClickedSquares, setRightClickedSquares] = useState({});
  const [syncedArrows, setSyncedArrows] = useState({});
  // Stable tuple array for react-chessboard — only rebuilt when arrows change,
  // so an unrelated re-render can't make an arrow flicker.
  const arrowTuples = useMemo(
    () => Object.values(syncedArrows).map(a => [a.from, a.to, a.color]),
    [syncedArrows]
  );
  const lastInternalArrowsRef = useRef([]);

  // ── Derived from the move tree ────────────────────────────────
  const pathIds   = useMemo(() => pathTo(nodes, currentId), [nodes, currentId]);
  const viewPly   = pathIds.length - 1;
  const curNode   = nodes[currentId] || nodes.r;
  // At the end of a line = no continuation stored. Students may only move
  // here; branching off mid-line is a coaching action.
  const atLineEnd = !(curNode?.children?.length);
  // How long the line we're currently on is, for the "4 / 12" readout.
  const lineLength = useMemo(
    () => pathTo(nodes, lineEnd(nodes, currentId)).length - 1,
    [nodes, currentId]
  );
  const isVariation = !!(curNode?.parent && (nodes[curNode.parent]?.children || []).indexOf(currentId) > 0);

  const rightGestureTsRef = useRef(0);   // when the right mouse button was last used (arrow gestures)
  const modifierKeyRef = useRef("default"); // which color modifier (alt/shift) was held during the gesture
  const arrowStartRef = useRef(null);    // square where a right-drag arrow began
  const lastWasDragRef = useRef(false);  // did the last right gesture drag between squares?

  // Modal states
  const [showPgnModal, setShowPgnModal] = useState(false);
  const [promotionMove, setPromotionMove] = useState(null); // {src,tgt} waiting for promotion choice
  const [showFenModal, setShowFenModal] = useState(false);
  const [showDbModal, setShowDbModal] = useState(false);   // import-from-database picker
  const [dbTopics, setDbTopics] = useState(null);          // null = not fetched yet
  const [dbSel, setDbSel] = useState(null);                // selected topic
  const [dbSearch, setDbSearch] = useState("");

  // ── Load class ─────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, "classes", classId));
      if (!snap.exists()) { navigate("/"); return; }
      const data = { id: snap.id, ...snap.data() };
      setClassData(data);
      if (isAdmin) {
        // Always reset startedAt when admin enters class
        const now = new Date().toISOString();
        await updateDoc(doc(db, "classes", classId), { startedAt: now, status: "live" });
        startTimeRef.current = Date.now();
      } else if (data.startedAt) {
        startTimeRef.current = new Date(data.startedAt).getTime();
      }
    }
    load();
  }, [classId]);

  // ── Timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    const id = setInterval(() => {
      if (startTimeRef.current) setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isAdmin]);

  function fmt(s) {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
    return `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
  }

  // ── RTDB refs ──────────────────────────────────────────────────
  const boardDbRef = ref(rtdb, `classes/${classId}/board`);
  const squareMarksDbRef = ref(rtdb, `classes/${classId}/annotations/squares`);
  const arrowMarksDbRef = ref(rtdb, `classes/${classId}/annotations/arrows`);
  const controlDbRef = ref(rtdb, `classes/${classId}/control`);
  const chatDbRef = ref(rtdb, `classes/${classId}/chat`);
  const statusDbRef = ref(rtdb, `classes/${classId}/status`);
  const annotationsDbRef = ref(rtdb, `classes/${classId}/annotations`);

  // ── Listen to board + control + chat + status ──────────────────
  useEffect(() => {
    // Sync square marks + arrows between admin and students
    onValue(squareMarksDbRef, snap => {
      setRightClickedSquares(snap.val() || {});
    });
    onValue(arrowMarksDbRef, snap => {
      setSyncedArrows(snap.val() || {});
    });

    onValue(boardDbRef, snap => {
      const d = snap.val(); if (!d) return;
      // New format carries the whole variation tree. Anything written by an
      // older build only has { fen, moveHistory } — rebuild a linear tree from
      // it so classes mid-flight don't break.
      let nextNodes, nextId;
      if (d.nodes) {
        nextNodes = normalizeNodes(d.nodes, d.rootFen || STARTING_FEN);
        nextId = d.currentId && nextNodes[d.currentId] ? d.currentId : lineEnd(nextNodes, "r");
      } else {
        const built = treeFromSans(d.rootFen || STARTING_FEN, d.moveHistory || []);
        nextNodes = built.nodes;
        nextId = built.tipId;
      }
      const shownFen = nextNodes[nextId]?.fen || d.fen || STARTING_FEN;
      setNodes(nextNodes);
      setCurrentId(nextId);          // everyone — coach and students — sits on the same node
      setFen(shownFen);
      try { const g = new Chess(); g.load(shownFen); setGame(g); } catch {}
    });
    onValue(controlDbRef, snap => {
      const d = snap.val(); if (!d) return;
      if (d.permissions !== undefined) setPermissions(d.permissions || {});
      if (d.allowIllegal !== undefined) setAllowIllegal(d.allowIllegal);
      if (d.boardOrientation !== undefined) setBoardOrientation(d.boardOrientation);
      if (d.editMode !== undefined) setEditMode(d.editMode);
      if (d.showHints !== undefined) setShowHints(d.showHints);
    });
    onValue(chatDbRef, snap => {
      const d = snap.val();
      if (!d) { setChatMessages([]); return; }
      const msgs = Object.values(d).sort((a, b) => a.ts - b.ts);
      setChatMessages(msgs);
    });
    // Listen for class ended (student gets kicked)
    onValue(statusDbRef, snap => {
      const d = snap.val();
      if (d === "ended" && !isAdmin) navigate("/student", { replace: true });
    });
    return () => { off(boardDbRef); off(controlDbRef); off(chatDbRef); off(statusDbRef); off(squareMarksDbRef); off(arrowMarksDbRef); };
  }, [classId]);

  // Auto scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // ── Check game result after every fen change ───────────────────
  useEffect(() => {
    // Only evaluate the end of a line — browsing back through moves (or into a
    // variation that continues) must never pop a result card.
    if (!atLineEnd) { setGameResult(null); setShowResult(false); return; }
    // While the coach is setting up a position in edit mode, never pop a
    // result — a half-built position is often a "draw" or has no kings yet.
    if (editMode) { setGameResult(null); setShowResult(false); return; }
    try {
      const g = new Chess(fen);
      if (g.isCheckmate()) {
        const winner = g.turn() === "w" ? "Black" : "White";
        setGameResult({ label: "Checkmate!", score: winner === "White" ? "1-0" : "0-1", winner });
        setShowResult(true);
      } else if (g.isDraw()) {
        let reason = "Draw";
        if (g.isStalemate()) reason = "Stalemate";
        else if (g.isThreefoldRepetition()) reason = "Threefold Repetition";
        else if (g.isInsufficientMaterial()) reason = "Insufficient Material";
        setGameResult({ label: reason, score: "½-½", winner: null });
        setShowResult(true);
      } else {
        setGameResult(null);
      }
    } catch {}
  }, [fen, atLineEnd, editMode]);

  // ── Push board ─────────────────────────────────────────────────
  // Writes the whole tree plus which node everyone should be looking at.
  // `fen` and `moveHistory` are still written so an older client (or the
  // student dashboard) reading this node keeps seeing something sensible.
  async function pushTree(nextNodes, nextId) {
    const shownFen = nextNodes[nextId]?.fen || STARTING_FEN;
    await set(boardDbRef, {
      fen: shownFen,
      moveHistory: mainlineSans(nextNodes),
      rootFen: nextNodes.r?.fen || STARTING_FEN,
      currentId: nextId,
      nodes: nextNodes,
    });
  }
  // Cheap write for pure navigation — no need to resend the whole tree.
  async function pushCurrent(nextId, shownFen) {
    try { await update(boardDbRef, { currentId: nextId, fen: shownFen }); } catch {}
  }
  // A new move only touches two nodes, so send just those. Keeps writes small
  // on a class full of phones instead of re-uploading the whole game each move.
  async function pushNewMove(nextNodes, newNode) {
    try {
      await update(boardDbRef, {
        [`nodes/${newNode.id}`]: newNode,
        [`nodes/${newNode.parent}/children`]: nextNodes[newNode.parent].children,
        rootFen: nextNodes.r?.fen || STARTING_FEN,
        currentId: newNode.id,
        fen: newNode.fen,
        moveHistory: mainlineSans(nextNodes),
      });
    } catch { pushTree(nextNodes, newNode.id); }
  }

  // Arrows and square marks belong to the position they were drawn on, so
  // any move or navigation wipes them. One client does the delete; everyone
  // else just sees the annotations node go empty.
  function clearShapes() {
    try { remove(annotationsDbRef); } catch {}
    lastInternalArrowsRef.current = [];
  }

  // Add `san` as a child of `parentId`. If that move already exists on this
  // node we just walk into it instead of duplicating; if the node already has
  // a continuation the new move becomes a variation (children[1..]).
  function addMoveNode(parentId, san, newFen) {
    const parent = nodes[parentId] || nodes.r;
    const twin = (parent.children || []).find(cid => nodes[cid]?.san === san);
    if (twin) return { nodes, id: twin, existed: true };
    const id = newNodeId();
    const next = { ...nodes };
    next[id] = { id, san, fen: newFen, parent: parent.id, children: [], ply: (parent.ply || 0) + 1 };
    next[parent.id] = { ...parent, children: [...(parent.children || []), id] };
    return { nodes: next, id, existed: false };
  }

  // Single funnel for "a move was just played" — keeps local state, Firebase
  // and the annotation layer in step no matter which code path produced it.
  function commitMove(san, newFen, chessObj) {
    const { nodes: next, id, existed } = addMoveNode(currentId, san, newFen);
    setNodes(next);
    setCurrentId(id);
    setFen(newFen);
    if (chessObj) setGame(chessObj);
    clearShapes();
    if (existed) pushCurrent(id, newFen);   // walked into a line we already had
    else pushNewMove(next, next[id]);       // brand-new move → send just the delta
  }

  // Replace the position of the node we're sitting on (edit mode / free drag).
  function commitFenInPlace(newFen) {
    const next = { ...nodes, [currentId]: { ...(nodes[currentId] || nodes.r), fen: newFen } };
    setNodes(next);
    setFen(newFen);
    clearShapes();   // board changed → old arrows/marks no longer apply
    try {
      update(boardDbRef, {
        [`nodes/${currentId}/fen`]: newFen,
        rootFen: next.r?.fen || STARTING_FEN,
        fen: newFen,
      });
    } catch { pushTree(next, currentId); }
  }

  // Start a brand-new tree from a position (reset / FEN / PGN / database).
  function commitNewTree(nextNodes, nextId) {
    const f = nextNodes[nextId]?.fen || STARTING_FEN;
    setNodes(nextNodes);
    setCurrentId(nextId);
    setFen(f);
    try { const g = new Chess(); g.load(f); setGame(g); } catch {}
    clearShapes();
    pushTree(nextNodes, nextId);
  }
  async function pushControl(updates) {
    // update() only writes the keys passed in. set() previously replaced the
    // WHOLE control node from this client's local React state — so if the
    // admin clicked any toolbar button before the control listener had synced
    // (e.g. right after joining), per-student permissions and showHints were
    // silently reset to "none"/false for the student. update() can never
    // wipe settings that this call doesn't explicitly change.
    await update(controlDbRef, updates);
  }

  // This student's own move permission (teacher always has full control)
  const myPermission = permissions[user?.uid] || "none";

  // ── Chess moves ────────────────────────────────────────────────
  function canMove(piece) {
    if (isAdmin) return true;
    // Coach is showing a position that already has a continuation — the class
    // is watching a line, so students don't get to branch off it.
    if (!atLineEnd) return false;
    if (myPermission === "none") return false;
    if (myPermission === "both") return true;
    const isWhite = piece[0] === "w";
    return (myPermission === "white" && isWhite) || (myPermission === "black" && !isWhite);
  }

  // Teacher: set one student's move permission (deep path → no clobber of others)
  function setStudentPermission(uid, perm) {
    if (!isAdmin) return;
    setPermissions(prev => ({ ...prev, [uid]: perm }));
    pushControl({ [`permissions/${uid}`]: perm });
  }

  function onDrop(src, tgt, piece) {
    // react-chessboard v5 passes a single object instead of three args
    if (typeof src === "object" && src !== null) {
      const args = src;
      piece = typeof args.piece === "string" ? args.piece : args.piece?.pieceType;
      tgt = args.targetSquare;
      src = args.sourceSquare;
      if (!src || !tgt || !piece) return false;
    }
    if (!canMove(piece)) return false;
    // Custom position (no kings) — allow free drag for anyone with permission
    const noKings = !fen.includes("K") || !fen.includes("k");
    if ((allowIllegal || noKings) && !editMode) {
      try {
        const pInfo = getPieceAt(fen, src);
        if (!pInfo) return false;
        const newFen = editSquare(editSquare(fen, src, "erase"), tgt,
          pInfo.color === "w" ? "w" + pInfo.type.toUpperCase() : "b" + pInfo.type.toUpperCase());
        commitFenInPlace(newFen); return true;
      } catch { return false; }
    }

    if (editMode && isAdmin) {
      try {
        // Get piece from source using board array
        const fenParts = fen.split(" ");
        const rows = fenParts[0].split("/");
        const board = rows.map(row => {
          const cells = [];
          for (const ch of row) {
            if (isNaN(ch)) { cells.push(ch); }
            else { for (let i = 0; i < parseInt(ch); i++) cells.push(null); }
          }
          return cells;
        });
        const srcCol = src.charCodeAt(0) - 97;
        const srcRow = 8 - parseInt(src[1]);
        const piece = board[srcRow][srcCol];
        if (!piece) return false;
        board[srcRow][srcCol] = null;
        const tgtCol = tgt.charCodeAt(0) - 97;
        const tgtRow = 8 - parseInt(tgt[1]);
        board[tgtRow][tgtCol] = piece;
        const newBoardFen = board.map(row => {
          let fenRow = ""; let empty = 0;
          for (const cell of row) {
            if (cell === null) { empty++; }
            else { if (empty > 0) { fenRow += empty; empty = 0; } fenRow += cell; }
          }
          if (empty > 0) fenRow += empty;
          return fenRow;
        }).join("/");
        fenParts[0] = newBoardFen;
        const newFen = fenParts.join(" ");
        commitFenInPlace(newFen); return true;
      } catch(e) { console.log("Edit drop error:", e); return false; }
    }
    if (allowIllegal && isAdmin) {
      try {
        // Use direct FEN manipulation — works on any board including no-king positions
        const pieceChar = getPieceChar(fen, src);
        if (!pieceChar) return false;
        const pieceCode = pieceChar === pieceChar.toUpperCase() ? "w" + pieceChar : "b" + pieceChar.toUpperCase();
        const newFen = editSquare(editSquare(fen, src, "erase"), tgt, pieceCode);
        commitMove(`${src}-${tgt}`, newFen, null); return true;
      } catch(e) { console.log("Illegal move error:", e); return false; }
    }
    try {
      const g = new Chess(fen);
      const move = g.move({ from: src, to: tgt, promotion: "q" });
      if (!move) return false;
      commitMove(move.san, g.fen(), g); return true;
    } catch { return false; }
  }

  function onSquareClick(square) {
    square = sqName(square);
    if (!square) return;

    // Edit mode: place/erase pieces (admin only)
    if (editMode && isAdmin && selectedEditPiece) {
      try {
        const newFen = editSquare(fen, square, selectedEditPiece);
        commitFenInPlace(newFen);
      } catch(e) {}
      return;
    }

    // Deselect if clicking same square
    if (square === selectedSquare) {
      setSelectedSquare(null);
      return;
    }

    // If a piece is already selected, try to move there
    if (selectedSquare) {
      const moved = tryClickMove(selectedSquare, square);
      if (moved) {
        setSelectedSquare(null);
        return;
      }
      // Not a valid move — fall through so clicking another piece re-selects it
    }

    // Select this square if it has a piece the user can move,
    // or if hints are on (so students can explore legal moves)
    const piece = getPieceAt(fen, square);
    if (piece) {
      const pieceCode = (piece.color === "w" ? "w" : "b") + piece.type.toUpperCase();
      if (isAdmin || showHints || canMove(pieceCode)) {
        setSelectedSquare(square);
        return;
      }
    }

    setSelectedSquare(null);
  }

  function onSquareRightClick(square, e) {
    square = sqName(square);
    if (!square) return;
    // If the gesture was a drag between squares, it was an arrow — don't also
    // drop a square mark on the start square.
    if (lastWasDragRef.current) { lastWasDragRef.current = false; return; }
    // Both admin and student can mark squares — synced through Firebase.
    // Writes target the single square's node so two people marking at the
    // same time can never overwrite each other's marks.
    const markRef = ref(rtdb, `classes/${classId}/annotations/squares/${square}`);
    const existing = rightClickedSquares[square];

    // Which color did the coach ask for? Alt = red, Alt+Shift = blue, else gold.
    // react-chessboard may not pass the event, so fall back to the modifier
    // captured on pointer-down. (Students always get their fixed blue.)
    const modKey = isAdmin ? (e ? pickColorKey(e) : modifierKeyRef.current) : "default";
    const wantColor = isAdmin ? MARK_COLORS[modKey] : STUDENT_MARK_COLOR;

    if (existing) {
      // Students may only remove their own (blue) marks — never the admin's
      if (!isAdmin && existing.owner === "admin") return;
      // If the coach right-clicks the SAME square with a DIFFERENT color,
      // recolor it instead of erasing. Same color → erase.
      if (isAdmin && existing.backgroundColor !== wantColor) {
        set(markRef, { backgroundColor: wantColor, owner: "admin" });
      } else {
        remove(markRef);
      }
    } else {
      set(markRef, {
        backgroundColor: wantColor,
        owner: isAdmin ? "admin" : "student",
      });
    }
  }

  // Right-click-drag arrows. react-chessboard's built-in arrow layer is
  // local-only and auto-clears on any left click — so we mirror every newly
  // drawn arrow into RTDB and render the synced set as controlled
  // customArrows, which persist and appear on both sides.
  // ── Manual arrow drawing ───────────────────────────────────────
  // We drive arrows entirely ourselves via right-mouse-down/up and keep
  // Firebase as the single source of truth. react-chessboard's built-in
  // arrow layer is NOT used (no onArrowsChange), because a controlled
  // customArrows prop + the internal layer fight each other and cause the
  // flicker where an arrow vanishes right after you draw it.

  // Figure out the board square under a pointer event using pure geometry:
  // the bounding box of the board div + orientation. This is far more reliable
  // than reading data-square off whatever element happens to be on top (a piece
  // image or the arrow SVG overlay carry no square, which silently broke arrows).
  function squareFromPoint(clientX, clientY) {
    const boardEl = document.querySelector(".frame-board");
    if (!boardEl) return null;
    const rect = boardEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    let x = (clientX - rect.left) / rect.width;
    let y = (clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    let file = Math.floor(x * 8);   // 0..7 from left
    let rank = Math.floor(y * 8);   // 0..7 from top
    file = Math.max(0, Math.min(7, file));
    rank = Math.max(0, Math.min(7, rank));
    if (boardOrientation === "white") {
      const f = "abcdefgh"[file];
      const r = 8 - rank;
      return `${f}${r}`;
    } else {
      const f = "abcdefgh"[7 - file];
      const r = rank + 1;
      return `${f}${r}`;
    }
  }

  function writeArrow(from, to) {
    if (!from || !to || from === to) return;
    const key = `${from}${to}`;
    const arrowRef = ref(rtdb, `classes/${classId}/annotations/arrows/${key}`);
    const existing = syncedArrows[key];
    const modKey = isAdmin ? modifierKeyRef.current : "default";
    const wantColor = isAdmin ? ARROW_COLORS[modKey] : STUDENT_ARROW_COLOR;

    if (existing) {
      // student can't touch coach's arrow
      if (!isAdmin && existing.owner === "admin") return;
      // same arrow again, different color → recolor; same color → erase
      if (isAdmin && existing.color !== wantColor) {
        set(arrowRef, { from, to, color: wantColor, owner: "admin" });
      } else {
        remove(arrowRef);
      }
    } else {
      set(arrowRef, { from, to, color: wantColor, owner: isAdmin ? "admin" : "student" });
    }
  }

  function onBoardPointerDown(e) {
    if (e.button !== 2) return;            // right button only
    rightGestureTsRef.current = Date.now();
    modifierKeyRef.current = pickColorKey(e);
    arrowStartRef.current = squareFromPoint(e.clientX, e.clientY);
  }

  function onBoardPointerUp(e) {
    if (e.button !== 2) return;
    const from = arrowStartRef.current;
    const to = squareFromPoint(e.clientX, e.clientY);
    arrowStartRef.current = null;
    if (from && to && from !== to) {
      // a drag between two squares → arrow
      lastWasDragRef.current = true;
      writeArrow(from, to);
    } else {
      lastWasDragRef.current = false;
    }
    // from === to is a single-square right-click → handled by onSquareRightClick
  }

  function clearAnnotations() {
    if (!isAdmin) return;
    remove(ref(rtdb, `classes/${classId}/annotations`));
    lastInternalArrowsRef.current = [];
  }

  function tryClickMove(src, tgt) {
    const piece = getPieceAt(fen, src);
    if (!piece) return false;
    const pieceCode = (piece.color === "w" ? "w" : "b") + piece.type.toUpperCase();
    if (!canMove(pieceCode)) return false;

    // In illegal mode OR when position has no kings (custom board),
    // allow free movement for both admin AND student with permission
    const noKings = !fen.includes("K") || !fen.includes("k");
    if (allowIllegal || noKings) {
      try {
        // Erase source, place on target
        const newFen = editSquare(editSquare(fen, src, "erase"), tgt, piece.color === "w" ? "w" + piece.type.toUpperCase() : "b" + piece.type.toUpperCase());
        commitFenInPlace(newFen); return true;
      } catch { return false; }
    }

    try {
      const g = new Chess(fen);
      const isPromotion = piece.type === "p" &&
        ((piece.color === "w" && tgt[1] === "8") || (piece.color === "b" && tgt[1] === "1"));
      if (isPromotion) {
        // Only open the promotion dialog if the move is actually legal —
        // otherwise an illegal pawn click opened a dialog that did nothing.
        const legal = g.moves({ square: src, verbose: true }).some(m => m.to === tgt);
        if (!legal) return false;
        setPromotionMove({ src, tgt });
        return true;
      }
      const move = g.move({ from: src, to: tgt });
      if (!move) return false;
      commitMove(move.san, g.fen(), g); return true;
    } catch { return false; }
  }

  function getPieceAt(currentFen, square) {
    try {
      const rows = currentFen.split(" ")[0].split("/");
      const col = square.charCodeAt(0) - 97;
      const row = 8 - parseInt(square[1]);
      let c = 0;
      for (const ch of rows[row]) {
        if (isNaN(ch)) { if (c === col) return { color: ch === ch.toUpperCase() ? "w" : "b", type: ch.toLowerCase() }; c++; }
        else { c += parseInt(ch); }
      }
    } catch {}
    return null;
  }

  // Directly manipulate FEN board string to place/remove pieces
  // This works even on empty boards where Chess.js would reject the FEN
  function editSquare(currentFen, square, pieceCode) {
    const fenParts = currentFen.split(" ");
    const boardFen = fenParts[0];

    // Convert FEN board to 8x8 array
    const rows = boardFen.split("/");
    const board = rows.map(row => {
      const cells = [];
      for (const ch of row) {
        if (isNaN(ch)) { cells.push(ch); }
        else { for (let i = 0; i < parseInt(ch); i++) cells.push(null); }
      }
      return cells;
    });

    // Convert square (e.g. "e4") to row/col
    const col = square.charCodeAt(0) - 97; // a=0, h=7
    const row = 8 - parseInt(square[1]);   // rank 8 = row 0

    if (pieceCode === "erase") {
      board[row][col] = null;
    } else {
      // pieceCode like "wQ" -> "Q", "bq" -> "q"
      const color = pieceCode[0]; // w or b
      const type = pieceCode[1];  // K Q R B N P
      board[row][col] = color === "w" ? type.toUpperCase() : type.toLowerCase();
    }

    // Convert back to FEN
    const newBoardFen = board.map(row => {
      let fenRow = "";
      let empty = 0;
      for (const cell of row) {
        if (cell === null) { empty++; }
        else { if (empty > 0) { fenRow += empty; empty = 0; } fenRow += cell; }
      }
      if (empty > 0) fenRow += empty;
      return fenRow;
    }).join("/");

    fenParts[0] = newBoardFen;
    return fenParts.join(" ");
  }

  function getIllegalModeMoves(currentFen, square) {
    // Returns array of squares a piece can move to, ignoring all chess rules except board boundaries
    const piece = getPieceAt(currentFen, square);
    if (!piece) return [];
    const col = square.charCodeAt(0) - 97; // a=0 h=7
    const row = 8 - parseInt(square[1]);   // rank8=row0
    const squares = [];

    function addSq(r, c) {
      if (r < 0 || r > 7 || c < 0 || c > 7) return false;
      const sq = String.fromCharCode(97 + c) + (8 - r);
      squares.push(sq);
      return !getPieceAt(currentFen, sq); // false = blocked, stop sliding
    }

    const t = piece.type;
    const color = piece.color;

    if (t === "r" || t === "q") {
      // Rook moves: 4 directions
      for (let i = 1; i <= 7; i++) { if (!addSq(row-i, col)) break; }
      for (let i = 1; i <= 7; i++) { if (!addSq(row+i, col)) break; }
      for (let i = 1; i <= 7; i++) { if (!addSq(row, col+i)) break; }
      for (let i = 1; i <= 7; i++) { if (!addSq(row, col-i)) break; }
    }
    if (t === "b" || t === "q") {
      // Bishop moves: 4 diagonals
      for (let i = 1; i <= 7; i++) { if (!addSq(row-i, col+i)) break; }
      for (let i = 1; i <= 7; i++) { if (!addSq(row-i, col-i)) break; }
      for (let i = 1; i <= 7; i++) { if (!addSq(row+i, col+i)) break; }
      for (let i = 1; i <= 7; i++) { if (!addSq(row+i, col-i)) break; }
    }
    if (t === "n") {
      // Knight moves
      [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]].forEach(([dr,dc]) => addSq(row+dr, col+dc));
    }
    if (t === "k") {
      // King moves
      [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]].forEach(([dr,dc]) => addSq(row+dr, col+dc));
    }
    if (t === "p") {
      // Pawn moves
      const dir = color === "w" ? -1 : 1;
      const startRow = color === "w" ? 6 : 1;
      if (row+dir >= 0 && row+dir <= 7) {
        const fwdSq = String.fromCharCode(97+col) + (8-(row+dir));
        if (!getPieceAt(currentFen, fwdSq)) {
          squares.push(fwdSq);
          if (row === startRow) {
            const fwd2Sq = String.fromCharCode(97+col) + (8-(row+dir*2));
            if (!getPieceAt(currentFen, fwd2Sq)) squares.push(fwd2Sq);
          }
        }
        // Captures
        [-1,1].forEach(dc => {
          if (col+dc >= 0 && col+dc <= 7) {
            const capSq = String.fromCharCode(97+col+dc) + (8-(row+dir));
            if (getPieceAt(currentFen, capSq)) squares.push(capSq);
          }
        });
      }
    }
    // Filter out own pieces
    return squares.filter(sq => {
      const p = getPieceAt(currentFen, sq);
      return !p || p.color !== color;
    });
  }

  // Squares the selected piece can move to — works on any position.
  // Normal positions use chess.js; kingless / illegal-mode positions use
  // piece-movement rules only (chess.js rejects FENs without both kings).
  function getMoveTargets(currentFen, square) {
    const noKings = !currentFen.includes("K") || !currentFen.includes("k");
    if (allowIllegal || noKings) {
      return getIllegalModeMoves(currentFen, square);
    }
    try {
      const g = new Chess(currentFen);
      return g.moves({ square, verbose: true }).map(m => m.to);
    } catch {
      return getIllegalModeMoves(currentFen, square);
    }
  }

  function getPieceChar(currentFen, square) {
    try {
      const rows = currentFen.split(" ")[0].split("/");
      const col = square.charCodeAt(0) - 97;
      const row = 8 - parseInt(square[1]);
      let c = 0;
      for (const ch of rows[row]) {
        if (isNaN(ch)) { if (c === col) return ch; c++; }
        else { c += parseInt(ch); }
      }
    } catch {}
    return null;
  }

  function completePromotion(pieceType) {
    if (!promotionMove) return;
    const { src, tgt } = promotionMove;
    setPromotionMove(null);
    try {
      const g = new Chess(fen);
      const move = g.move({ from: src, to: tgt, promotion: pieceType });
      if (!move) return;
      commitMove(move.san, g.fen(), g);
    } catch {}
  }

  // Jump the board — and everyone watching — to a node in the tree.
  function goToNode(id) {
    const n = nodes[id];
    if (!n) return;
    const f = n.fen;
    setCurrentId(id);
    setFen(f);
    try { const g = new Chess(); g.load(f); setGame(g); } catch {}
    clearShapes();               // arrows/marks belong to the old position
    pushCurrent(id, f);
  }
  function goStart()   { goToNode("r"); }
  function stepBack()  { const p = nodes[currentId]?.parent; if (p) goToNode(p); }
  function stepForward(){ const c = nodes[currentId]?.children?.[0]; if (c) goToNode(c); }
  function goLatest()  { goToNode(lineEnd(nodes, currentId)); }

  // ── Variation management (coach only) ─────────────────────────
  // Make the line we're on the main continuation of its parent.
  function promoteVariation() {
    if (!isAdmin) return;
    const n = nodes[currentId];
    if (!n || !n.parent) return;
    const p = nodes[n.parent];
    const kids = [...(p.children || [])];
    const i = kids.indexOf(currentId);
    if (i <= 0) return;                       // already the main line here
    kids.splice(i, 1); kids.unshift(currentId);
    const next = { ...nodes, [p.id]: { ...p, children: kids } };
    setNodes(next);
    pushTree(next, currentId);
  }
  // Delete the current move and everything after it.
  function deleteVariation() {
    if (!isAdmin) return;
    const n = nodes[currentId];
    if (!n || !n.parent) return;              // never delete the root
    const doomed = new Set();
    (function walk(id) {
      doomed.add(id);
      (nodes[id]?.children || []).forEach(walk);
    })(currentId);
    const next = {};
    Object.values(nodes).forEach(x => {
      if (doomed.has(x.id)) return;
      next[x.id] = { ...x, children: (x.children || []).filter(c => !doomed.has(c)) };
    });
    const back = n.parent;
    setNodes(next);
    setCurrentId(back);
    setFen(next[back].fen);
    try { const g = new Chess(); g.load(next[back].fen); setGame(g); } catch {}
    clearShapes();
    pushTree(next, back);
  }

  function clearBoard() {
    const emptyFen = "8/8/8/8/8/8/8/8 w - - 0 1";
    const t = freshTree(emptyFen);
    setEditMode(true);
    commitNewTree(t.nodes, "r");
    pushControl({ editMode: true });
  }

  function resetBoard() {
    const t = freshTree(STARTING_FEN);
    commitNewTree(t.nodes, "r");
  }

  function loadPgn() {
    try {
      const g = new Chess(); g.loadPgn(pgnInput.trim());
      const history = g.history();
      const t = treeFromSans(STARTING_FEN, history);
      commitNewTree(t.nodes, t.tipId);
      setPgnMsg("✓ Loaded!");
      setPgnInput(""); setShowPgnModal(false);
      setTimeout(() => setPgnMsg(""), 2000);
    } catch { setPgnMsg("✗ Invalid PGN"); }
  }

  function loadFen() {
    try {
      const g = new Chess(); g.load(fenInput.trim());
      const t = freshTree(g.fen());
      commitNewTree(t.nodes, "r");
      setFenInput(""); setShowFenModal(false);
    } catch { alert("Invalid FEN string. Please check and try again."); }
  }

  // ── Import a position from the teaching Database ──
  function openDbModal() {
    setShowDbModal(true);
    if (dbTopics === null) {
      getDocs(collection(db, "database"))
        .then(snap => {
          const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          rows.sort((a, b) => (a.group || "").localeCompare(b.group || "") || a.name.localeCompare(b.name));
          setDbTopics(rows);
        })
        .catch(err => { console.error("Could not load database:", err); setDbTopics([]); });
    }
  }

  function loadDbPosition(p) {
    try {
      const g = new Chess(); g.load(p.fen);
      const t = freshTree(g.fen());
      commitNewTree(t.nodes, "r");
      setShowDbModal(false); setDbSel(null); setDbSearch("");
    } catch { alert("This position couldn't be loaded — its FEN looks invalid."); }
  }

  async function sendChat(e) {
    e.preventDefault();
    if (!chatInput.trim()) return;
    await push(chatDbRef, {
      text: chatInput.trim(),
      sender: isAdmin ? "Teacher" : (userData?.name || "Student"),
      isAdmin,
      uid: user?.uid || null,
      ts: Date.now()
    });
    setChatInput("");
  }

  useEffect(() => {
    // VIDEO TRANSPORT: our own Jitsi server (143-244-135-123.sslip.io).
    // Presence stays on Firebase exactly as before — the participant list,
    // tiles and move-permissions all read classes/{id}/participants.
    // Jitsi ONLY carries audio/video. Each person joins the Jitsi room with
    // their Firebase uid as display name, so incoming tracks can be filed
    // under remoteStreams[uid] — the exact shape the tiles already render.
    if (!classId || !user) return;
    let isMounted = true;
    const myUid = user.uid;

    let jitsiConnection = null;
    let jitsiRoom = null;
    let localTracks = [];

    if (isAdmin) {
      onDisconnect(ref(rtdb, `classes/${classId}/status`)).set("ended");
    }

    const participantsRef  = ref(rtdb, `classes/${classId}/participants`);
    const myParticipantRef = ref(rtdb, `classes/${classId}/participants/${myUid}`);

    const JITSI_DOMAIN = "143-244-135-123.sslip.io";
    const ROOM_NAME = `chessingit${classId}cgt92x`.toLowerCase();

    function setStreamFor(uid, stream) {
      setRemoteStreams(prev => (prev[uid] === stream ? prev : { ...prev, [uid]: stream }));
    }
    function setPeerState(uid, st) { setPeerStates(prev => ({ ...prev, [uid]: st })); }

    // jitsiId -> firebase uid (learned from display names)
    const idToUid = {};
    // uid -> { video?: track, audio?: track } so we can build one MediaStream per person
    const trackBuckets = {};

    function fileTrack(jitsiId, track) {
      const uid = idToUid[jitsiId];
      if (!uid) return; // name not known yet; onDisplayNameChanged will re-file
      const bucket = trackBuckets[uid] || (trackBuckets[uid] = {});
      bucket[track.getType()] = track;
      const parts = [];
      if (bucket.video) parts.push(bucket.video.getTrack());
      if (bucket.audio) parts.push(bucket.audio.getTrack());
      setStreamFor(uid, new MediaStream(parts));
      setPeerState(uid, "connected");
    }

    function learnName(jitsiId, name) {
      if (!name) return;
      idToUid[jitsiId] = name; // we set displayName = firebase uid
      // Re-file any tracks that arrived before we knew the name
      const p = jitsiRoom?.getParticipantById?.(jitsiId);
      if (p) p.getTracks().forEach(t => fileTrack(jitsiId, t));
    }

    async function startJitsi() {
      const J = window.JitsiMeetJS;
      if (!J) {
        console.error("lib-jitsi-meet not loaded — check the script tag in index.html");
        setCallStatus("error");
        return;
      }
      J.init({ disableAudioLevels: true });
      J.setLogLevel(J.logLevels.ERROR);

      jitsiConnection = new J.JitsiConnection(null, null, {
        hosts: {
          domain: JITSI_DOMAIN,
          muc: `conference.${JITSI_DOMAIN}`,
        },
        serviceUrl: `wss://${JITSI_DOMAIN}/xmpp-websocket?room=${ROOM_NAME}`,
      });

      jitsiConnection.addEventListener(
        J.events.connection.CONNECTION_ESTABLISHED,
        onConnected
      );
      jitsiConnection.addEventListener(
        J.events.connection.CONNECTION_FAILED,
        () => { if (isMounted) setCallStatus("error"); }
      );

      jitsiConnection.connect();

      async function onConnected() {
        if (!isMounted) return;
        jitsiRoom = jitsiConnection.initJitsiConference(ROOM_NAME, {});

        jitsiRoom.on(J.events.conference.TRACK_ADDED, track => {
          if (track.isLocal()) return;
          fileTrack(track.getParticipantId(), track);
        });
        jitsiRoom.on(J.events.conference.TRACK_REMOVED, track => {
          if (track.isLocal()) return;
          const uid = idToUid[track.getParticipantId()];
          if (!uid || !trackBuckets[uid]) return;
          delete trackBuckets[uid][track.getType()];
        });
        jitsiRoom.on(J.events.conference.USER_JOINED, (id, p) => {
          learnName(id, p?.getDisplayName?.());
        });
        jitsiRoom.on(J.events.conference.DISPLAY_NAME_CHANGED, (id, name) => {
          learnName(id, name);
        });
        jitsiRoom.on(J.events.conference.USER_LEFT, id => {
          const uid = idToUid[id];
          delete idToUid[id];
          if (uid) {
            delete trackBuckets[uid];
            setRemoteStreams(prev => { const n = { ...prev }; delete n[uid]; return n; });
            setPeerStates(prev => { const n = { ...prev }; delete n[uid]; return n; });
          }
        });
        jitsiRoom.on(J.events.conference.CONFERENCE_JOINED, async () => {
          if (!isMounted) return;
          jitsiRoom.setDisplayName(myUid); // key: lets others map our tracks to our uid
          try {
            localTracks = await J.createLocalTracks({ devices: ["audio", "video"] });
            for (const t of localTracks) {
              await jitsiRoom.addTrack(t);
              if (t.getType() === "video" && myVideoRef.current) {
                t.attach(myVideoRef.current);
              }
            }
            streamRef.current = new MediaStream(localTracks.map(t => t.getTrack()));
            if (isMounted) setCallStatus("ready");
          } catch (err) {
            console.error("Camera/mic error:", err);
            if (isMounted) setCallStatus("error");
          }
        });

        jitsiRoom.join();
      }
    }

    async function start() {
      try {
        if (!isAdmin) {
          const snap = await get(participantsRef);
          const parts = snap.val() || {};
          const studentCount = Object.values(parts).filter(p => p.role !== "admin").length;
          if (!parts[myUid] && studentCount >= MAX_STUDENTS) {
            alert(`This class is full (${MAX_STUDENTS} students max).`);
            navigate("/student", { replace: true });
            return;
          }
        }

        // Register presence (auto-cleared on disconnect) — exactly as before
        await set(myParticipantRef, {
          name: isAdmin ? "Teacher" : (userData?.name || "Student"),
          role: isAdmin ? "admin" : "student",
          joinedAt: Date.now(),
        });
        onDisconnect(myParticipantRef).remove();
        onValue(participantsRef, snap => setParticipants(snap.val() || {}));

        await startJitsi();
      } catch (err) {
        console.error("Video error:", err);
        setCallStatus("error");
      }
    }

    start();

    return () => {
      isMounted = false;
      off(participantsRef);
      try { localTracks.forEach(t => t.dispose()); } catch {}
      try { jitsiRoom && jitsiRoom.leave(); } catch {}
      try { jitsiConnection && jitsiConnection.disconnect(); } catch {}
      remove(myParticipantRef);
      setParticipants({});
      setRemoteStreams({});
      setPeerStates({});
    };
  }, [classId, isAdmin, user]);


  function toggleCam() {
    const t = streamRef.current?.getVideoTracks()[0];
    if (t) { t.enabled = !t.enabled; setCamOn(t.enabled); camOnRef.current = t.enabled; }
  }

  function toggleMic() {
    const t = streamRef.current?.getAudioTracks()[0];
    if (t) { t.enabled = !t.enabled; setMicOn(t.enabled); }
  }

  async function endClass() {
    if (!window.confirm("End the class for everyone?")) return;
    await updateDoc(doc(db, "classes", classId), { status: "ended" });
    await set(statusDbRef, "ended");
    navigate("/admin", { replace: true }); // replace so back button never returns to class
  }

  function leaveClass() {
    navigate("/student", { replace: true }); // replace so back button never returns to class
  }

  // ── Move list, lichess style ──────────────────────────────────
  // Follows children[0] to draw a line. Any extra children are alternatives to
  // the move just printed, so they're drawn in brackets straight after it —
  // 3. Bc4 Bc5 (3… Nf6 4. d3) — and recursively for nested lines.
  // A black move prints "3…" when it opens a line or follows a bracket.
  function renderLine(startId, depth) {
    const out = [];
    let id = startId;
    let needNum = true;
    let guard = 0;
    while (id && nodes[id] && guard++ < 2000) {
      const n = nodes[id];
      const nodeId = id;              // capture per iteration — `id` is reassigned below
      const no = Math.ceil(n.ply / 2);
      const isWhite = n.ply % 2 === 1;
      if (isWhite) out.push(<span key={`${nodeId}-n`} className="mv-num">{no}.</span>);
      else if (needNum) out.push(<span key={`${nodeId}-n`} className="mv-num">{no}…</span>);
      out.push(
        <button
          key={nodeId}
          className={`mv-san${currentId === nodeId ? " current" : ""}`}
          onClick={() => { if (isAdmin) goToNode(nodeId); }}
          title={isAdmin ? "Jump to this move" : undefined}
        >
          {n.san}
        </button>
      );
      needNum = false;
      const sibs = nodes[n.parent]?.children || [];
      if (sibs[0] === nodeId && sibs.length > 1) {
        sibs.slice(1).forEach(vid => {
          out.push(
            <span key={`${vid}-v`} className={`mv-var mv-d${Math.min(depth + 1, 3)}`}>
              <span className="mv-paren">(</span>
              {renderLine(vid, depth + 1)}
              <span className="mv-paren">)</span>
            </span>
          );
          needNum = true;   // line resumes → a black move needs its "N…" back
        });
      }
      id = (n.children || [])[0];
    }
    return out;
  }

  return (
    <div className="live-layout">

      {showResult && gameResult && (
        <div className="result-overlay">
          <div className="result-card">
            <div className="result-icon">{gameResult.winner === "White" ? "♔" : gameResult.winner === "Black" ? "♚" : "⚖"}</div>
            <div className="result-label">{gameResult.label}</div>
            <div className="result-score">{gameResult.score}</div>
            {gameResult.winner && <div className="result-winner">{gameResult.winner} wins</div>}
            <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => setShowResult(false)}>Close &amp; View Board</button>
          </div>
        </div>
      )}

      {promotionMove && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 320, textAlign: "center" }}>
            <h3 style={{ marginBottom: 16 }}>Promote Pawn</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>Choose a piece to promote to:</p>
            <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
              {[
                { type: "q", sym: "♕", label: "Queen" },
                { type: "r", sym: "♖", label: "Rook" },
                { type: "b", sym: "♗", label: "Bishop" },
                { type: "n", sym: "♘", label: "Knight" },
              ].map(p => (
                <button key={p.type} onClick={() => completePromotion(p.type)}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                    background: "var(--surface2)", border: "2px solid var(--border)",
                    borderRadius: 10, padding: "12px 16px", cursor: "pointer",
                    fontSize: 36, color: "var(--text)", transition: "all 0.15s"
                  }}
                  onMouseOver={e => e.currentTarget.style.borderColor = "var(--accent)"}
                  onMouseOut={e => e.currentTarget.style.borderColor = "var(--border)"}
                >
                  {p.sym}
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showPgnModal && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowPgnModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Load PGN</h3>
            <textarea value={pgnInput} onChange={e => setPgnInput(e.target.value)} rows={10} placeholder="1. e4 e5 2. Nf3 Nc6 ..." style={{ fontFamily: "monospace", fontSize: 12 }} />
            {pgnMsg && <p style={{ color: pgnMsg.startsWith("✓") ? "var(--success)" : "var(--danger)", fontSize: 13, marginTop: 6 }}>{pgnMsg}</p>}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowPgnModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={loadPgn}>Load PGN</button>
            </div>
          </div>
        </div>
      )}

      {showFenModal && isAdmin && (
        <div className="modal-overlay" onClick={() => setShowFenModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Load FEN</h3>
            <input value={fenInput} onChange={e => setFenInput(e.target.value)} placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1" />
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setShowFenModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={loadFen}>Load FEN</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import from Database ── */}
      {showDbModal && isAdmin && (
        <div className="modal-overlay" onClick={() => { setShowDbModal(false); setDbSel(null); }}>
          <div className="modal dbp-modal" onClick={e => e.stopPropagation()}>
            <h3>🗄️ Import from Database</h3>

            {dbTopics === null && <p className="dbp-loading">Loading topics…</p>}
            {dbTopics !== null && dbTopics.length === 0 && (
              <p className="dbp-loading">The database is empty — run build-database.mjs on your computer to fill it.</p>
            )}

            {dbTopics !== null && dbTopics.length > 0 && !dbSel && (
              <>
                <input className="dbp-search" placeholder="🔎 Search topics…" value={dbSearch}
                  onChange={e => setDbSearch(e.target.value)} />
                <div className="dbp-topics">
                  {dbTopics
                    .filter(t => !dbSearch.trim() || t.name.toLowerCase().includes(dbSearch.trim().toLowerCase()))
                    .map(t => (
                      <button key={t.id} className="dbp-topic" onClick={() => setDbSel(t)}>
                        <span className="dbp-topic-name">{t.name}</span>
                        <span className="dbp-topic-sub">{t.group} · {t.totalPositions}</span>
                      </button>
                    ))}
                </div>
              </>
            )}

            {dbSel && (
              <>
                <button className="btn btn-secondary dbp-back" onClick={() => setDbSel(null)}>← Topics</button>
                <div className="dbp-positions">
                  {(dbSel.positions || []).map((p, i) => (
                    <div key={i} className="dbp-pos">
                      <div className="dbp-pos-info">
                        <span className="dbp-pos-num">#{i + 1}</span>
                        <span className="dbp-pos-turn">{p.colorToMove === "w" ? "⚪ White" : "⚫ Black"} to move</span>
                        {p.note && <span className="dbp-pos-note">{p.note}</span>}
                        {p.solution && p.solution.length > 0 && <span className="dbp-pos-sol">{p.solution.length}-move solution</span>}
                      </div>
                      <button className="btn btn-primary dbp-load" onClick={() => loadDbPosition(p)}>Load →</button>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="modal-actions" style={{ marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={() => { setShowDbModal(false); setDbSel(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="live-topbar">
        <div className="live-topbar-left">
          <img src={LOGO_SRC} alt="ChessingIt" style={{ height: 36, width: "auto", objectFit: "contain" }} />
          <span className="live-class-title">{classData?.title || "Live Class"}</span>
          {isAdmin && <span className="live-timer">⏱ {fmt(elapsed)}</span>}
          <span className="tag tag-live">● LIVE</span>
        </div>
        <div className="live-topbar-right">
          {isAdmin
            ? <button className="btn btn-danger" onClick={endClass}>End Class</button>
            : <button className="btn btn-secondary" onClick={leaveClass}>Leave</button>
          }
        </div>
      </div>

      <div className="live-body">
        {isAdmin && (
          <aside className="live-sidebar">
            <div className="sidebar-section-label">Tools</div>
            <Tip side="right" title="Load PGN game">
              <button className="tool-btn" onClick={() => setShowPgnModal(true)}>
                <span>📋</span><span className="tool-label">PGN</span>
              </button>
            </Tip>
            <Tip side="right" title="Load position from FEN">
              <button className="tool-btn" onClick={() => setShowFenModal(true)}>
                <span>🔢</span><span className="tool-label">FEN</span>
              </button>
            </Tip>
            <Tip side="right" title="Import a position from the Database">
              <button className="tool-btn" onClick={openDbModal}>
                <span>🗄️</span><span className="tool-label">Database</span>
              </button>
            </Tip>
            <Tip side="right" title="Edit board — place pieces freely">
              <button className={`tool-btn ${editMode ? "active" : ""}`}
                onClick={() => { const newEdit = !editMode; setEditMode(newEdit); setSelectedEditPiece(null); pushControl({ editMode: newEdit }); }}>
                <span>✏️</span><span className="tool-label">Edit</span>
              </button>
            </Tip>
            <Tip side="right" title={`Illegal moves: ${allowIllegal ? "ON" : "OFF"}`}>
              <button className={`tool-btn ${allowIllegal ? "active" : ""}`}
                onClick={() => { setAllowIllegal(!allowIllegal); pushControl({ allowIllegal: !allowIllegal }); }}>
                <span>⚡</span><span className="tool-label">Illegal</span>
              </button>
            </Tip>
            <Tip side="right" title="Flip board orientation">
              <button className="tool-btn"
                onClick={() => { const o = boardOrientation === "white" ? "black" : "white"; setBoardOrientation(o); pushControl({ boardOrientation: o }); }}>
                <span>🔄</span><span className="tool-label">Flip</span>
              </button>
            </Tip>
            <Tip side="right" title="Reset to starting position">
              <button className="tool-btn" onClick={resetBoard}>
                <span>🔁</span><span className="tool-label">Reset</span>
              </button>
            </Tip>
            <Tip side="right" title="Clear board — remove all pieces">
              <button className="tool-btn" onClick={clearBoard}>
                <span>🗑️</span><span className="tool-label">Clear</span>
              </button>
            </Tip>
            <Tip side="right" title="Clear all arrows and square marks">
              <button className="tool-btn" onClick={clearAnnotations}>
                <span>🧹</span><span className="tool-label">Marks</span>
              </button>
            </Tip>
            <Tip side="right" title={`Show legal moves for student: ${showHints ? "ON" : "OFF"}`}>
              <button className={`tool-btn ${showHints ? "active" : ""}`}
                onClick={() => { setShowHints(!showHints); pushControl({ showHints: !showHints }); }}>
                <span>💡</span><span className="tool-label">Legal Moves</span>
              </button>
            </Tip>
          </aside>
        )}

        <div className="live-board-col">
          <div className="board-stack">
          <div className="board-wrapper"
            onPointerDownCapture={onBoardPointerDown}
            onPointerUpCapture={onBoardPointerUp}
            onContextMenu={e => e.preventDefault()}
          >
            <div className="board-frame">
              <div className="frame-ranks">
                {(boardOrientation === "white" ? [8, 7, 6, 5, 4, 3, 2, 1] : [1, 2, 3, 4, 5, 6, 7, 8]).map(r => (
                  <span key={r} className="frame-coord">{r}</span>
                ))}
              </div>
              <div className="frame-board">
                <Chessboard
                  position={fen}
                  onPieceDrop={onDrop}
                  isDraggablePiece={({ piece }) => canMove(piece)}
                  onSquareClick={onSquareClick}
                  boardOrientation={boardOrientation}
                  arePiecesDraggable={true}
                  animateOnSetPosition={true}
                  showBoardNotation={false}
                  customArrows={arrowTuples}
                  areArrowsAllowed={false}
                  customSquareStyles={(() => {
                    const h = {};
                    // strip `owner` — react-chessboard only wants style props
                    Object.entries(rightClickedSquares).forEach(([sq, v]) => {
                      h[sq] = { backgroundColor: v?.backgroundColor || v };
                    });
                    if (!selectedSquare) return h;
                    h[selectedSquare] = { background: "rgba(255,215,0,0.35)", borderRadius: "4px" };
                    // Admin always sees hints; student sees them when "Legal Moves" is ON
                    const canSee = isAdmin || showHints;
                    if (canSee) {
                      const targets = getMoveTargets(fen, selectedSquare);
                      targets.forEach(sq => {
                        h[sq] = getPieceAt(fen, sq) ? {
                          background: "radial-gradient(circle, transparent 56%, rgba(255,215,0,0.85) 56%, rgba(255,215,0,0.85) 72%, transparent 72%)",
                          borderRadius: "50%"
                        } : {
                          background: "radial-gradient(circle, rgba(255,215,0,0.7) 28%, transparent 28%)",
                          borderRadius: "50%"
                        };
                      });
                    }
                    return h;
                  })()}
                  onSquareRightClick={onSquareRightClick}
                  customBoardStyle={{ borderRadius: "4px", boxShadow: "inset 0 0 18px rgba(0,0,0,0.25)" }}
                  customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
                  customDarkSquareStyle={{ backgroundColor: "#b58863" }}
                />
              </div>
              <div className="frame-corner" />
              <div className="frame-files">
                {(boardOrientation === "white" ? ["A", "B", "C", "D", "E", "F", "G", "H"] : ["H", "G", "F", "E", "D", "C", "B", "A"]).map(f => (
                  <span key={f} className="frame-coord">{f}</span>
                ))}
              </div>
            </div>
          </div>

          {isAdmin && (
            <div className="board-nav">
              <Tip title="Go to start (move 0)"><button className="nav-btn" onClick={goStart} disabled={viewPly === 0}>⟪</button></Tip>
              <Tip title="Previous move"><button className="nav-btn" onClick={stepBack} disabled={viewPly === 0}>‹</button></Tip>
              <span className="nav-info">{viewPly} / {lineLength}</span>
              <Tip title="Next move"><button className="nav-btn" onClick={stepForward} disabled={atLineEnd}>›</button></Tip>
              <Tip title="Go to end of this line"><button className="nav-btn" onClick={goLatest} disabled={atLineEnd}>⟫</button></Tip>
              {isVariation && (
                <Tip title="Promote this line to the main line">
                  <button className="nav-btn nav-promote" onClick={promoteVariation}>⤴</button>
                </Tip>
              )}
              {viewPly > 0 && (
                <Tip title="Delete this move and everything after it">
                  <button className="nav-btn nav-delete" onClick={deleteVariation}>✕</button>
                </Tip>
              )}
            </div>
          )}
          </div>

          {isAdmin && editMode && (
            <div className="piece-palette-bar">
              <span className="palette-bar-label">Place piece:</span>
              {[
                { code: "wK", sym: "♔" }, { code: "wQ", sym: "♕" }, { code: "wR", sym: "♖" },
                { code: "wB", sym: "♗" }, { code: "wN", sym: "♘" }, { code: "wP", sym: "♙" },
                { code: "bK", sym: "♚" }, { code: "bQ", sym: "♛" }, { code: "bR", sym: "♜" },
                { code: "bB", sym: "♝" }, { code: "bN", sym: "♞" }, { code: "bP", sym: "♟" },
              ].map(p => (
                <button key={p.code}
                  className={`palette-bar-piece ${selectedEditPiece === p.code ? "selected" : ""}`}
                  onClick={() => setSelectedEditPiece(selectedEditPiece === p.code ? null : p.code)}
                  title={p.code}
                >{p.sym}</button>
              ))}
              <button
                className={`palette-bar-erase ${selectedEditPiece === "erase" ? "active" : ""}`}
                onClick={() => setSelectedEditPiece(selectedEditPiece === "erase" ? null : "erase")}
                title="Click squares to erase pieces">
                🗑 Erase{selectedEditPiece === "erase" ? " (ON)" : ""}
              </button>
            </div>
          )}

        </div>

        <div className="live-right-col">
          <div className="video-section-head">
            In class
            <span className="student-count">
              {Object.values(participants).filter(p => p.role !== "admin").length}/{MAX_STUDENTS} students
            </span>
          </div>
          {isAdmin && Object.keys(participants).length > 2 && (
            <div className="tile-search-wrap">
              <input
                className="tile-search"
                placeholder="🔎 Search students…"
                value={tileSearch}
                onChange={e => setTileSearch(e.target.value)}
              />
              {tileSearch && (
                <button className="tile-search-clear" onClick={() => setTileSearch("")}>×</button>
              )}
            </div>
          )}
          <div className="video-grid">
            {/* Your own tile */}
            <div className="vtile">
              <div className="video-box">
                {callStatus === "error"
                  ? <div className="video-placeholder"><span>⚠️</span><p>Camera unavailable</p></div>
                  : <video ref={myVideoRef} autoPlay muted playsInline className="video-el" />}
                <div className="video-label">{isAdmin ? "👑 You" : `${userData?.name || "You"}`}</div>
                <div className="video-controls">
                  <button className={`vid-btn ${camOn ? "" : "off"}`} onClick={toggleCam}>{camOn ? "📷" : "🚫"}</button>
                  <button className={`vid-btn ${micOn ? "" : "off"}`} onClick={toggleMic}>{micOn ? "🎤" : "🔇"}</button>
                </div>
              </div>
            </div>

            {/* Everyone else */}
            {Object.entries(participants)
              .filter(([uid]) => uid !== user?.uid)
              .filter(([, p]) => !tileSearch.trim() || (p.name || "").toLowerCase().includes(tileSearch.trim().toLowerCase()))
              .sort((a, b) => (a[1].role === "admin" ? -1 : b[1].role === "admin" ? 1 : 0))
              .map(([uid, p]) => {
                const stream = remoteStreams[uid];
                const perm = permissions[uid] || "none";
                return (
                  <div className="vtile" key={uid}>
                    <div className="video-box">
                      {stream
                        ? <video autoPlay playsInline className="video-el"
                            ref={el => { if (el && el.srcObject !== stream) { el.srcObject = stream; el.play().catch(() => {}); } }} />
                        : <div className="video-placeholder"><div className="spinner" /><p>Connecting…</p></div>}
                      <div className="video-label">
                        <span className={`conn-dot ${peerStates[uid] === "connected" ? "ok" : ""}`} />
                        {p.role === "admin" ? "👑 " : ""}{p.name || "Student"}
                      </div>
                    </div>
                    {/* Teacher controls each student's move permission */}
                    {isAdmin && p.role !== "admin" && (
                      <div className="perm-strip" role="group" aria-label={`Move permission for ${p.name || "student"}`}>
                        {PERMS.map(pm => (
                          <button key={pm}
                            className={`perm-btn ${perm === pm ? "active" : ""}`}
                            title={pm === "none" ? "Cannot move" : pm === "both" ? "Move both colors" : `Move ${pm}`}
                            onClick={() => setStudentPermission(uid, pm)}>
                            {PERM_ICON[pm]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

            {Object.keys(participants).filter(uid => uid !== user?.uid).length === 0 && (
              <div className="vtile">
                <div className="video-box">
                  <div className="video-placeholder">
                    <span>📡</span><p>{isAdmin ? "Waiting for students" : "Waiting for others"}</p>
                  </div>
                </div>
              </div>
            )}
          </div>


          <div className="right-panel">
            <div className="right-tabs">
              <button className={`right-tab ${rightTab === "moves" ? "active" : ""}`} onClick={() => setRightTab("moves")}>Moves</button>
              <button className={`right-tab ${rightTab === "chat" ? "active" : ""}`} onClick={() => setRightTab("chat")}>
                Chat {chatMessages.length > 0 && <span className="chat-badge">{chatMessages.length}</span>}
              </button>
              {isAdmin && <button className={`right-tab ${rightTab === "options" ? "active" : ""}`} onClick={() => setRightTab("options")}>Options</button>}
            </div>

            {rightTab === "moves" && (
              <div className="moves-panel">
                {!(nodes.r?.children || []).length
                  ? <p className="no-moves">No moves yet</p>
                  : <div className="moves-flow">{renderLine(nodes.r.children[0], 0)}</div>
                }
              </div>
            )}

            {rightTab === "chat" && (
              <div className="chat-panel">
                <div className="chat-messages">
                  {chatMessages.length === 0 && <p className="no-moves">No messages yet</p>}
                  {chatMessages.map((msg, i) => {
                    // Mine → right; others → left. Old messages without a
                    // uid fall back to the admin/student split.
                    const mine = (msg.uid != null && user?.uid != null)
                      ? msg.uid === user.uid
                      : msg.isAdmin === isAdmin;
                    return (
                      <div key={i} className={`chat-msg ${mine ? "admin-msg" : "student-msg"}`}>
                        <span className="chat-sender">{msg.isAdmin ? "👑 " : ""}{msg.sender}</span>
                        <span className="chat-text">{msg.text}</span>
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
                <form onSubmit={sendChat} className="chat-form">
                  <input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Type a message..." className="chat-input" />
                  <button type="submit" className="btn btn-primary chat-send">→</button>
                </form>
              </div>
            )}

            {rightTab === "options" && isAdmin && (
              <div className="options-panel">
                <div className="option-section">
                  <label>Current FEN</label>
                  <div className="fen-display">{fen}</div>
                  <button className="btn btn-secondary" style={{ width:"100%", marginTop:6, fontSize:12 }}
                    onClick={() => navigator.clipboard.writeText(fen)}>Copy FEN</button>
                </div>
                <div className="option-section">
                  <label>Export PGN</label>
                  <button className="btn btn-secondary" style={{ width:"100%", fontSize:12 }}
                    onClick={() => { try { navigator.clipboard.writeText(treeToPgn(nodes)); alert("PGN copied (variations included)!"); } catch {} }}>
                    Copy PGN to Clipboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
